import React, { useState } from 'react';
import { Prospect } from '../../shared/types';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { QrCode, Share2, Award, ArrowLeft, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ResellerDemoProps {
  prospect: Prospect;
}

export const ResellerDemo: React.FC<ResellerDemoProps> = ({ prospect }) => {
  const [activeTab, setActiveTab] = useState<'revendedora' | 'admin'>('revendedora');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans">
      {/* Top Banner Notice */}
      <div className="bg-indigo-950/80 border-b border-indigo-500/30 py-2.5 px-4 sticky top-0 z-50 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <Badge variant="amber" className="uppercase font-bold text-[10px]">
              Protótipo de Módulo Opcional
            </Badge>
            <span className="hidden sm:inline text-amber-200">
              Solução proposta para a <strong>{prospect.trade_name}</strong> — Ainda não contratado
            </span>
          </div>
          <Link
            to={`/${prospect.slug}`}
            className="inline-flex items-center gap-1.5 font-bold text-indigo-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Voltar para Proposta Comercial
          </Link>
        </div>
      </div>

      <header className="py-8 border-b border-slate-800 bg-slate-900/60">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-bold text-teal-400 uppercase tracking-wider">
                Módulo Futuro de Expansão
              </span>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white">
                Sistema de Revendedoras & Afiliadas {prospect.trade_name}
              </h1>
            </div>

            {/* Switch View Mode */}
            <div className="flex items-center p-1 rounded-xl bg-slate-800 border border-slate-700">
              <button
                onClick={() => setActiveTab('revendedora')}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  activeTab === 'revendedora'
                    ? 'bg-teal-500 text-slate-950 font-extrabold shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Visão da Revendedora
              </button>
              <button
                onClick={() => setActiveTab('admin')}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                  activeTab === 'admin'
                    ? 'bg-teal-500 text-slate-950 font-extrabold shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Visão da Gestão
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Prototype Warning Card */}
        <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 mb-8 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-300 leading-relaxed">
            <strong>Demonstração de Conceito:</strong> Este protótipo visual simula a plataforma exclusiva onde revendedoras da {prospect.trade_name} poderão gerar links individuais, compartilhar catálogos em PDF/WhatsApp e acompanhar comissões de vendas.
          </p>
        </div>

        {activeTab === 'revendedora' ? (
          <div className="space-y-8">
            {/* User Profile Summary */}
            <Card className="border-teal-500/30 bg-slate-900/80">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-teal-500/20 text-teal-300 font-extrabold flex items-center justify-center text-xl border border-teal-500/40">
                    AM
                  </div>
                  <div>
                    <h3 className="text-lg font-extrabold text-white">Ana Maria (Revendedora Nível Ouro)</h3>
                    <span className="text-xs text-slate-400">Código Único: TURQUESA-ANA2026</span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Button variant="whatsapp" size="sm" icon={<Share2 className="w-4 h-4" />}>
                    Compartilhar Meu Link Exclusivo
                  </Button>
                  <Button variant="outline" size="sm" icon={<QrCode className="w-4 h-4" />}>
                    Ver QR Code
                  </Button>
                </div>
              </div>
            </Card>

            {/* Key Metrics for Reseller */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-slate-800 text-center">
                <span className="text-xs text-slate-400 block mb-1">Vendas este Mês</span>
                <span className="text-2xl font-extrabold text-white">R$ 4.850,00</span>
              </Card>
              <Card className="border-emerald-500/30 text-center">
                <span className="text-xs text-emerald-400 block mb-1">Comissão a Receber (15%)</span>
                <span className="text-2xl font-extrabold text-emerald-400">R$ 727,50</span>
              </Card>
              <Card className="border-slate-800 text-center">
                <span className="text-xs text-slate-400 block mb-1">Clientes Atendidas</span>
                <span className="text-2xl font-extrabold text-white">18</span>
              </Card>
              <Card className="border-slate-800 text-center">
                <span className="text-xs text-slate-400 block mb-1">Meta do Mês (Ouro)</span>
                <span className="text-2xl font-extrabold text-purple-400">82%</span>
              </Card>
            </div>

            {/* Reseller Features List */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-slate-800">
                <h4 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-teal-400" /> Ferramentas de Compartilhamento
                </h4>
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    Catálogo Digital interativo com o seu nome e contato.
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    Links diretos para carrinho pré-montado no WhatsApp.
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    Cupons de desconto personalizados para suas clientes (ex: `ANA10`).
                  </li>
                </ul>
              </Card>

              <Card className="border-slate-800">
                <h4 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-purple-400" /> Gamificação & Ranking
                </h4>
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    Níveis de comissão progressivos (Bronze 10%, Prata 12%, Ouro 15%).
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    Bônus por metas atingidas no mês e premiações em peças.
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    Histórico de comissões e pagamentos transparente.
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        ) : (
          /* Admin View Mode */
          <div className="space-y-6">
            <Card className="border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white text-base">Rede de Revendedoras Cadastradas</h3>
                <Badge variant="indigo">14 Revendedoras Ativas</Badge>
              </div>

              <div className="divide-y divide-slate-800 text-xs">
                <div className="py-3 flex items-center justify-between">
                  <div>
                    <strong className="text-white block">Ana Maria Silva</strong>
                    <span className="text-slate-400">São Miguel do Oeste • Nível Ouro</span>
                  </div>
                  <div className="text-right">
                    <span className="text-emerald-400 font-bold">R$ 4.850,00 vendas/mês</span>
                    <span className="block text-[11px] text-slate-500">18 pedidos realizados</span>
                  </div>
                </div>
                <div className="py-3 flex items-center justify-between">
                  <div>
                    <strong className="text-white block">Juliana Ribeiro</strong>
                    <span className="text-slate-400">Maravilha / SC • Nível Prata</span>
                  </div>
                  <div className="text-right">
                    <span className="text-emerald-400 font-bold">R$ 2.400,00 vendas/mês</span>
                    <span className="block text-[11px] text-slate-500">9 pedidos realizados</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
